<template>
  <CModal 
    :show.sync="visible" 
    size="lg" 
    @hidden="onHidden"
    :close-on-backdrop="false"
  >
    <template #header>
      <h5 class="modal-title">
        <translate translate-context="contract_manager.delete">Delete Contract Manager</translate>
      </h5>
      <CButtonClose @click="close" />
    </template>
    <div class="modal-body">
      <div v-if="loading" class="text-center py-4">
        <CSpinner color="primary" />
      </div>
      
      <div v-else-if="dependencies">
        <CAlert v-if="dependencies.blocked_by_payouts" color="danger">
          <CIcon name="cipWarning" size="lg" class="mr-2" />
          <translate translate-context="contract_manager.delete">
            This Contract Manager cannot be deleted because it has contracts with payout items. These must be handled manually first.
          </translate>
        </CAlert>
        
        <CAlert v-else-if="!dependencies.requires_force" color="info">
          <translate translate-context="contract_manager.delete">
            This Contract Manager has no dependencies and can be safely deleted.
          </translate>
        </CAlert>
        
        <CAlert v-else color="warning">
          <translate translate-context="contract_manager.delete">
            This Contract Manager has dependencies. Review them carefully before proceeding.
          </translate>
        </CAlert>

        <div v-if="dependencies.contracts_with_payouts && dependencies.contracts_with_payouts.length > 0" class="mb-4">
          <h6 class="text-danger">
            <CIcon name="cipBan" size="lg" class="mr-2" />
            <translate translate-context="contract_manager.delete">Contracts with Payouts (Blocking Deletion)</translate>
            <CBadge color="danger" class="ml-2">{{ dependencies.contracts_with_payouts.length }}</CBadge>
          </h6>
          <CAlert color="danger" size="sm">
            <translate translate-context="contract_manager.delete">
              These contracts contain payout items and CANNOT be deleted. You must handle these manually before you can delete this Contract Manager.
            </translate>
          </CAlert>
          <CListGroup>
            <CListGroupItem v-for="contract in dependencies.contracts_with_payouts" :key="`cwp-${contract.id}`" class="border-danger">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong class="text-danger">{{ contract.name }}</strong>
                  <div class="small text-muted">
                    <CIcon name="cipMoney" size="sm" class="text-danger" />
                    <span class="text-danger font-weight-bold">
                      <translate translate-context="contract_manager.delete">Has payout items - Must be handled manually</translate>
                    </span>
                  </div>
                </div>
                <CButton 
                  size="sm" 
                  color="danger" 
                  variant="outline"
                  @click="navigateToContract(contract.id)"
                >
                  <translate translate-context="contract_manager.delete">View Contract</translate>
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <div v-if="dependencies.picaservices && dependencies.picaservices.length > 0" class="mb-4">
          <h6>
            <translate translate-context="contract_manager.delete">PicaServices that will be disassociated</translate>
            <CBadge color="info" class="ml-2">{{ dependencies.picaservices.length }}</CBadge>
          </h6>
          <CListGroup>
            <CListGroupItem v-for="service in dependencies.picaservices" :key="`ps-${service.id}`">
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ service.name }}</span>
                <CButton 
                  size="sm" 
                  color="link" 
                  @click="navigateToPicaService(service.event_id)"
                >
                  <translate translate-context="contract_manager.delete">View Event</translate>
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <div v-if="dependencies.contracts && dependencies.contracts.length > 0" class="mb-4">
          <h6>
            <translate translate-context="contract_manager.delete">Non-empty Contracts (with invoices/payouts) that will be deleted</translate>
            <CBadge color="danger" class="ml-2">{{ dependencies.contracts.length }}</CBadge>
          </h6>
          <CAlert color="danger" size="sm">
            <translate translate-context="contract_manager.delete">
              Warning: These contracts contain invoices or payout items and will be permanently deleted!
            </translate>
          </CAlert>
          <CListGroup>
            <CListGroupItem v-for="contract in dependencies.contracts" :key="`c-${contract.id}`">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ contract.name }}</strong>
                  <div class="small text-muted">
                    <span v-if="contract.has_invoices" class="mr-2">
                      <CIcon name="cipFile" size="sm" />
                      <translate translate-context="contract_manager.delete">Has invoices</translate>
                    </span>
                    <span v-if="contract.has_payout_items">
                      <CIcon name="cipMoney" size="sm" />
                      <translate translate-context="contract_manager.delete">Has payouts</translate>
                    </span>
                  </div>
                </div>
                <CButton 
                  size="sm" 
                  color="link" 
                  @click="navigateToContract(contract.id)"
                >
                  <translate translate-context="contract_manager.delete">View Contract</translate>
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <div v-if="dependencies.empty_contracts && dependencies.empty_contracts.length > 0" class="mb-4">
          <h6>
            <translate translate-context="contract_manager.delete">Empty Contracts that will be deleted</translate>
            <CBadge color="warning" class="ml-2">{{ dependencies.empty_contracts.length }}</CBadge>
          </h6>
          <CListGroup>
            <CListGroupItem v-for="contract in dependencies.empty_contracts" :key="`ec-${contract.id}`">
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ contract.name }}</span>
                <CButton 
                  size="sm" 
                  color="link" 
                  @click="navigateToContract(contract.id)"
                >
                  <translate translate-context="contract_manager.delete">View Contract</translate>
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <div v-if="dependencies.user_roles && dependencies.user_roles.length > 0" class="mb-4">
          <h6>
            <translate translate-context="contract_manager.delete">User Roles that will be deleted</translate>
            <CBadge color="warning" class="ml-2">{{ dependencies.user_roles.length }}</CBadge>
          </h6>
          <CListGroup>
            <CListGroupItem v-for="role in dependencies.user_roles" :key="`r-${role.id}`">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ role.name }}</strong>
                  <div v-if="role.description" class="small text-muted" v-html="role.description"></div>
                </div>
                <CButton 
                  size="sm" 
                  color="link" 
                  @click="navigateToRole(role.id)"
                >
                  <translate translate-context="contract_manager.delete">View Role</translate>
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <div v-if="dependencies.actions_if_forced && !dependencies.blocked_by_payouts && dependencies.requires_force" class="mt-4 p-3 bg-light rounded">
          <h6>
            <translate translate-context="contract_manager.delete">Summary of actions if you proceed:</translate>
          </h6>
          <ul class="mb-0">
            <li v-if="dependencies.actions_if_forced.disassociate && dependencies.actions_if_forced.disassociate.picaservices">
              <translate 
                translate-context="contract_manager.delete"
                :translate-params="{count: dependencies.actions_if_forced.disassociate.picaservices}"
              >
                %{count} PicaService(s) will be disassociated
              </translate>
            </li>
            <li v-if="dependencies.actions_if_forced.delete">
              <span v-if="dependencies.actions_if_forced.delete.contracts">
                <translate 
                  translate-context="contract_manager.delete"
                  :translate-params="{count: dependencies.actions_if_forced.delete.contracts}"
                >
                  %{count} non-empty contract(s) will be deleted
                </translate>
              </span>
            </li>
            <li v-if="dependencies.actions_if_forced.delete && dependencies.actions_if_forced.delete.empty_contracts">
              <translate 
                translate-context="contract_manager.delete"
                :translate-params="{count: dependencies.actions_if_forced.delete.empty_contracts}"
              >
                %{count} empty contract(s) will be deleted
              </translate>
            </li>
            <li v-if="dependencies.actions_if_forced.delete && dependencies.actions_if_forced.delete.user_roles">
              <translate 
                translate-context="contract_manager.delete"
                :translate-params="{count: dependencies.actions_if_forced.delete.user_roles}"
              >
                %{count} user role(s) will be deleted
              </translate>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <template #footer>
      <CButton color="secondary" @click="close">
        <translate translate-context="contract_manager.delete">Cancel</translate>
      </CButton>
      <CButton 
        v-if="dependencies && !loading && dependencies.blocked_by_payouts"
        color="danger" 
        disabled
      >
        <CIcon name="cipBan" size="sm" class="mr-1" />
        <translate translate-context="contract_manager.delete">Cannot Delete - Payouts Exist</translate>
      </CButton>
      <CButton 
        v-else-if="dependencies && !loading"
        color="danger" 
        @click="onConfirmDelete"
        :disabled="deleting"
      >
        <CSpinner v-if="deleting" size="sm" class="mr-1" />
        <translate translate-context="contract_manager.delete">Proceed with Deletion</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ContractManagerDeleteModal',
  
  data() {
    return {
      visible: false,
      contractManagerId: null,
      contractManagerName: null,
      loading: false,
      deleting: false,
    }
  },
  
  computed: {
    ...mapGetters('contractManager', ['deleteDependencies']),
    dependencies() {
      return this.deleteDependencies
    }
  },
  
  methods: {
    ...mapActions('contractManager', [
      'checkContractManagerDependencies',
      'deleteContractManager'
    ]),
    
    async open(contractManager) {
      this.contractManagerId = contractManager.id
      this.contractManagerName = contractManager.name
      this.loading = true
      this.visible = true
      
      try {
        await this.checkContractManagerDependencies(contractManager.id)
      } catch (error) {
        this.notifyError(this.$pgettext('contract_manager.delete', 'Failed to check dependencies'))
        this.close()
      } finally {
        this.loading = false
      }
    },
    
    close() {
      this.visible = false
    },
    
    onHidden() {
      this.contractManagerId = null
      this.contractManagerName = null
      this.loading = false
      this.deleting = false
    },
    
    async onConfirmDelete() {
      if (this.dependencies.blocked_by_payouts) {
        this.notifyError(this.$pgettext('contract_manager.delete', 'Cannot delete: contracts with payout items must be handled manually first'))
        return
      }
      
      this.deleting = true
      try {
        await this.deleteContractManager({
          id: this.contractManagerId,
          force: this.dependencies.requires_force
        })
        await this.notifySuccess(this.$pgettext('contract_manager.delete', 'Contract Manager deleted successfully'))
        this.close()
        this.$emit('deleted')
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          if (error.response.data.error === 'blocked_by_payouts') {
            await this.notifyError(this.$pgettext('contract_manager.delete', 'Cannot delete: contracts with payout items must be handled manually first'))
          } else if (error.response.data.error === 'requires_force') {
            await this.notifyError(this.$pgettext('contract_manager.delete', 'This Contract Manager has dependencies that require force deletion'))
          } else {
            await this.notifyError(this.$pgettext('contract_manager.delete', 'Failed to delete Contract Manager'))
          }
        } else {
          await this.notifyError(this.$gettext('Failed to delete Contract Manager', {}, 'contract_manager.delete'))
        }
      } finally {
        this.deleting = false
      }
    },
    
    navigateToPicaService(eventId) {
      window.open(this.$router.resolve({
        name: 'event.overview',
        params: { id: eventId }
      }).href, '_blank')
    },
    
    navigateToContract(contractId) {
      window.open(this.$router.resolve({
        name: 'contract_manager.contracts',
        params: { id: this.contractManagerId },
        query: { contract: contractId }
      }).href, '_blank')
    },
    
    navigateToRole(roleId) {
      window.open(this.$router.resolve({
        name: 'contract_manager.roles',
        params: { id: this.contractManagerId },
        query: { role: roleId }
      }).href, '_blank')
    }
  }
}
</script>